import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth/AuthProvider";
import { StoreProvider } from "./store/StoreContext";
import { AdminShell } from "./shells/admin/AdminShell";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { BrandSettings } from "./pages/settings/BrandSettings";
import { CustomFieldsPage } from "./pages/settings/CustomFields";
import { MessagingSettings } from "./pages/settings/Messaging";
import { MessagesPage } from "./pages/Messages";
import { PackagesSettings } from "./pages/settings/Packages";
import { PayReturn } from "./public/PayReturn";
import { CalendarPage } from "./pages/Calendar";
import { AppointmentsPage } from "./pages/Appointments";
import { CustomersPage } from "./pages/Customers";
import { ServicesPage } from "./pages/Services";
import { StaffPage } from "./pages/Staff";
import { ShiftsPage } from "./pages/Shifts";
import { LeavePage } from "./pages/Leave";
import { AttendancePage } from "./pages/Attendance";
import { ExpensesPage } from "./pages/Expenses";
import { UsersPage } from "./pages/settings/Users";
import { ResourcesPage } from "./pages/Resources";
import { CustomerArea } from "./customer/CustomerArea";
import { BookingWizard } from "./public/BookingWizard";
import { ManageBooking } from "./public/ManageBooking";
import { WaitlistClaim } from "./public/WaitlistClaim";
import { InvitePage } from "./public/Invite";

// heavy pages (TinyMCE, Leaflet) load on demand so the main bundle stays small
const NotificationsSettings = lazy(() => import("./pages/settings/Notifications").then((m) => ({ default: m.NotificationsSettings })));
const StoreSettings = lazy(() => import("./pages/settings/StoreSettings").then((m) => ({ default: m.StoreSettings })));
const Lazy = ({ children }: { children: JSX.Element }) => <Suspense fallback={<p className="p-4 text-gray-500">…</p>}>{children}</Suspense>;

function Protected({ children }: { children: JSX.Element }) {
  const { me, ready } = useAuth();
  const { t } = useTranslation();
  if (!ready) return <div className="p-8 text-gray-500">{t("app.loading")}</div>;
  return me ? <StoreProvider>{children}</StoreProvider> : <Navigate to="/login" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/me/*" element={<CustomerArea />} />
      <Route path="/book/:storeSlug" element={<BookingWizard />} />
      <Route path="/b/:token" element={<ManageBooking />} />
      <Route path="/w/:token" element={<WaitlistClaim />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/pay/:paymentId" element={<PayReturn />} />
      <Route path="/app" element={<Protected><AdminShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="shifts" element={<ShiftsPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="settings/users" element={<UsersPage />} />
        <Route path="settings/store/:storeId" element={<Lazy><StoreSettings /></Lazy>} />
        <Route path="settings/notifications/:brandId" element={<Lazy><NotificationsSettings /></Lazy>} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="settings/brand/:brandId" element={<BrandSettings />} />
        <Route path="settings/custom-fields" element={<CustomFieldsPage />} />
        <Route path="settings/messaging/:brandId" element={<MessagingSettings />} />
        <Route path="settings/packages/:brandId" element={<PackagesSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
